import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO component for managing page metadata
 * Sets proper title, description, and other meta tags for each page
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - Social sharing image URL
 * @param {Object} props.schema - JSON-LD structured data
 */
const SEO = ({ 
  title = 'Professional Translation Services',
  description = 'Professional translation services for gaming, software, and technical content. High-quality localization services in multiple languages.',
  image = '/natux-banner.jpg',
  schema = null
}) => {
  const location = useLocation();
  const siteUrl = 'https://natuxtranslations.com';
  const fullTitle = `Natux Translations - ${title}`;
  const fullUrl = `${siteUrl}${location.pathname}`;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  return (
    <Helmet>
      {/* Basic metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />
      
      {/* JSON-LD structured data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
